<template>
    <div class="productCarousel" :class="classCarousel">
        <CarouselBox
            v-if="loaded && products.length"
            :products="products"
            :title="title"
            :class-name="className"
            :type-item="typeItem"
            :show-pre-basket="showPreBasket"
            :item-list-name="itemListName"
        />
        <SkeletonProductCarouselSkeleton v-if="!loaded" :max-number-of-products="productsInSkeleton" />
    </div>
</template>

<script lang="ts">
import { PropType } from 'vue'
import CarouselBox from '~/components/elements/CarouselBox.vue'
import LuigisBoxEventApi from '~/extensions/LuigisBoxEventApi'
import { transformProductsToLuigisBoxItems } from '~/utils/transformProductsToLuigisBoxItems'
import Product from '~/models/Product/Product'

export default {
    components: {
        CarouselBox,
    },

    props: {
        ids: {
            type: Array as PropType<number[]>,
            default: null,
        },
        title: {
            type: String,
            default: '',
        },
        className: {
            type: String,
            default: '',
        },
        classCarousel: {
            type: String,
            default: '',
        },
        typeItem: {
            type: String,
            default: 'slim-carousel__item--product',
        },
        showPreBasket: {
            type: Boolean,
            default: true,
        },
        availableOnly: {
            type: Boolean,
            default: false,
        },
        inStockOnly: {
            type: Boolean,
            default: false,
        },
        productsInSkeleton: {
            type: Number,
            default: 6,
        },
        itemListName: {
            type: String,
            default: '',
        },
        luigisBoxCarouselType: {
            type: String,
            default: '',
        },
        luigisBoxItemIds: {
            type: String,
            default: '',
        },
        isLuigisBoxVariant: {
            type: Boolean,
            default: false,
        },
        preserveIdsOrder: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            products: [] as Product[],
            loaded: false,
        }
    },

    watch: {
        ids: function () {
            this.refresh()
        },
    },

    mounted(): void {
        this.refresh()
    },

    methods: {
        async refresh() {
            this.products = []
            if (this.ids === null) {
                return
            }

            if (this.ids.length === 0) {
                this.loaded = true

                return
            }

            try {
                const response = await this.$repository.getProductsByIds(this.ids, this.preserveIdsOrder, useUidCookie().value)
                this.products = response.products

                if (this.availableOnly) {
                    this.products = this.products.filter((product: Product) => product.isAvailable)
                }

                if (this.inStockOnly) {
                    this.products = this.products.filter((product: Product) => product.amountInStock > 0)
                }

                if (this.luigisBoxCarouselType !== '') {
                    LuigisBoxEventApi.sendRecommendationEvent(
                        transformProductsToLuigisBoxItems(this.products),
                        this.title,
                        this.luigisBoxCarouselType,
                        this.luigisBoxItemIds,
                        this.isLuigisBoxVariant
                    )
                }
            } catch {
                this.products = []
            }

            this.loaded = true
        },
    },
}
</script>

<style lang="scss" scoped>
/*
Structure:
    .productCarousel(.productCarousel--home)
        .productCarousel__tabs
            .productCarousel__title
 */

.productCarousel {
    position: relative;
    max-width: $max-width-container-inside;
    margin: 2em auto;

    &--home {
        margin-bottom: 0;
    }
}

.productCarousel__title {
    font-size: 18px;
    text-align: left;
    margin-right: 10px;
    margin-top: 0.5rem;
    margin-bottom: 0.75em;
    color: $color-dark;
    font-weight: 500;

    @media (min-width: 768px) {
        float: left;
        margin-right: 20px;
    }
}

.title {
    margin-right: 1em;
    font-size: 18px;
    color: $color-dark;
    padding-left: 0;
    font-weight: 500;

    a {
        color: $color-dark;

        &:hover {
            color: $color-cta;
        }
    }

    @media (min-width: 768px) {
        float: left;
        position: relative;
        z-index: 2;
    }
}
</style>

<style lang="scss" scoped>
.productCarousel .nav {
    flex-wrap: nowrap;
    overflow-x: auto;
}
</style>
